/* eslint-disable react/require-default-props */
import React, { useEffect, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { createScriptNode } from '../utils/dom';

interface BeamerWindow extends Window {
  beamer_config?: {
    post_request: boolean;
    filter_by_url: boolean;
    language: string;
    user_id: number;
    user_email?: string;
    product_id: string;
    selector: string;
    left: number;
    top: number;
  };
}

const w = window as BeamerWindow;

interface Props {
  email?: string;
  accountId?: number;
}

const BEAMER_ID = 'OKAVqgha7213';
const BEAMER_ELEMENT_ID = 'beamer-script-loader';

const BeamerWidget: React.FC<Props> = ({ email, accountId }) => {
  const { locale } = useIntl();
  const isScriptLoaded = useRef(false);
  const isInitialized = useRef(false);
  const loadTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Preconnect to Beamer domains
  useEffect(() => {
    const preconnectLinks = [
      { rel: 'preconnect', href: 'https://app.getbeamer.com' },
      { rel: 'preconnect', href: 'https://realtime.getbeamer.com' },
      { rel: 'preconnect', href: 'https://push.getbeamer.com' },
      { rel: 'preconnect', href: 'https://backend.getbeamer.com' },
      { rel: 'dns-prefetch', href: 'https://app.getbeamer.com' },
      { rel: 'dns-prefetch', href: 'https://realtime.getbeamer.com' },
      { rel: 'dns-prefetch', href: 'https://push.getbeamer.com' },
      { rel: 'dns-prefetch', href: 'https://backend.getbeamer.com' },
    ];

    preconnectLinks.forEach(({ rel, href }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      preconnectLinks.forEach(({ rel, href }) => {
        const link = document.querySelector(
          `link[rel="${rel}"][href="${href}"]`
        );
        if (link) link.remove();
      });
    };
    // eslint-disable-next-line consistent-return
  }, []);

  const initializeBeamer = useCallback(() => {
    if (!accountId || isInitialized.current) return;

    w.beamer_config = {
      post_request: true,
      filter_by_url: true,
      language: locale,
      user_id: accountId,
      user_email: email,
      product_id: BEAMER_ID,
      selector: '#beamer-widget',
      left: 12,
      top: 10,
    };
    isInitialized.current = true;
  }, [accountId, email, locale]);

  const loadBeamerScript = useCallback(() => {
    if (isScriptLoaded.current) return;

    const script = document.getElementById(BEAMER_ELEMENT_ID);
    if (script) {
      initializeBeamer();
      return;
    }

    const scriptNode = createScriptNode({
      src: 'https://app.getbeamer.com/js/beamer-embed.js',
      id: BEAMER_ELEMENT_ID,
      async: true,
      defer: true,
      onload: () => {
        isScriptLoaded.current = true;
        initializeBeamer();
      },
    });
    document.body.appendChild(scriptNode);
  }, [initializeBeamer]);

  // Load Beamer based on scroll position or user interaction
  useEffect(() => {
    if (!isScriptLoaded.current) {
      const loadOnScroll = () => {
        // Load when user scrolls to bottom 20% of the page
        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;
        const threshold = documentHeight * 0.8;

        if (scrollPosition >= threshold) {
          if (loadTimeout.current) {
            clearTimeout(loadTimeout.current);
          }
          loadTimeout.current = setTimeout(loadBeamerScript, 1000);
          window.removeEventListener('scroll', loadOnScroll);
        }
      };

      const loadOnInteraction = () => {
        if (loadTimeout.current) {
          clearTimeout(loadTimeout.current);
        }
        loadTimeout.current = setTimeout(loadBeamerScript, 1000);
        document.removeEventListener('mousemove', loadOnInteraction);
        document.removeEventListener('click', loadOnInteraction);
        document.removeEventListener('keydown', loadOnInteraction);
      };

      // Add scroll listener
      window.addEventListener('scroll', loadOnScroll, { passive: true });

      // Add interaction listeners
      const events = ['mousemove', 'click', 'keydown'];
      events.forEach((event) => {
        document.addEventListener(event, loadOnInteraction, { passive: true });
      });

      // Also load if user is idle for 15 seconds
      const idleTimer = setTimeout(loadBeamerScript, 15000);

      // Cleanup function
      return () => {
        window.removeEventListener('scroll', loadOnScroll);
        events.forEach((event) => {
          document.removeEventListener(event, loadOnInteraction);
        });
        if (loadTimeout.current) {
          clearTimeout(loadTimeout.current);
        }
        clearTimeout(idleTimer);
      };
    }

    // Return empty cleanup function when script is already loaded
    return () => {};
  }, [loadBeamerScript]);

  if (window.Cypress) {
    return null;
  }
  return null;
};

export default BeamerWidget;
